import { useEffect, useRef, useState } from "react";
import { api } from "../../api";
import { ErrorNote, Loading } from "../../components/Feedback";
import { focusWhenReady } from "../../focus";
import { Btn, Card, Field, Input } from "../../components/Shared";
import { useApi } from "../../useApi";
import { SuccessNote } from "./parts";

// Management -> Time zone (task 2026-10-21 "the portal has a time zone", review fixes the same
// day; backend routers/admin_timezone.py). GET /api/admin/timezone {timezone, default,
// hosted_on_other_zone: [{project, database, version, zone}]}; GET .../names the IANA names;
// PUT {timezone} -> the GET body. The setting is the zone NEW design versions freeze: each
// built or hosted database keeps the zone its version was frozen with until its next design
// version is migrated (the plan says "changes from X to Y"). No stored instant changes.

export default function TimezoneCard() {
  const r = useApi("/admin/timezone");
  const names = useApi("/admin/timezone/names");
  const [value, setValue] = useState("");
  const [saved, setSaved] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const formRef = useRef(null);
  const data = saved || r.data;

  useEffect(() => { if (r.data) setValue(r.data.timezone); }, [r.data]);

  const save = async e => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    // Save is disabled while saving and stays disabled once the value equals the saved zone, so
    // keyboard focus would fall to <body>; give it to the field (2026-10-21 browser check). Not
    // rememberFocus: the Save button it remembers is focusable again for a frame before it is
    // disabled by the saved value, and would take the focus only to lose it.
    try {
      setSaved(await api.put("/admin/timezone", { timezone: value.trim() }));
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
      focusWhenReady(() => formRef.current && formRef.current.querySelector("input"));
    }
  };

  if (r.loading) return <Loading />;
  if (r.error) return <ErrorNote error={r.error} />;
  const list = names.data || [];
  const unchanged = value.trim() === data.timezone;
  const others = data.hosted_on_other_zone || [];
  return (
    <Card>
      <h2 style={{ margin: "0 0 12px", fontSize: 18, fontWeight: 700 }}>Time zone</h2>
      <ErrorNote error={error} />
      {saved && (
        <SuccessNote>
          Saved: design versions frozen from now on use {saved.timezone}. No database changed.
        </SuccessNote>
      )}
      <form ref={formRef} onSubmit={save} style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
        <Field label="Time zone" style={{ flex: "1 1 240px", minWidth: 0 }}
               hint={`An IANA name such as Europe/Vienna or UTC (default ${data.default}).`}>
          <Input value={value} onChange={e => setValue(e.target.value)} list="unio-timezones"
                 autoComplete="off" spellCheck={false} />
        </Field>
        <datalist id="unio-timezones">
          {list.map(n => <option key={n} value={n} />)}
        </datalist>
        <Btn type="submit" disabled={busy || !value.trim() || unchanged}>{busy ? "Saving…" : "Save"}</Btn>
      </form>
      <div style={{ fontSize: 13, color: "#4b5563", margin: "14px 0 0" }}>
        <p style={{ margin: "0 0 6px" }}>
          A date and time without an offset (<code>2026-09-01 10:00</code>) in an uploaded table, an
          import, a row form or the API is read as a local time in the zone of its design version
          (this one, for versions frozen from now on); one with <code>Z</code> or an offset
          (<code>+02:00</code>) keeps it. Project databases store instants (<code>timestamptz</code>)
          and show them in their zone, with the offset.
        </p>
        <p style={{ margin: 0 }}>
          A change applies to <strong>new design versions</strong>. Every built or hosted database
          keeps the zone its design version was frozen with, for everything it reads and shows,
          until its next design version is built or migrated; the migration plan then says
          “the time zone changes from … to …”. No stored time changes.
        </p>
      </div>
      {others.length > 0 && (
        <div data-testid="hosted-on-other-zone" style={{ fontSize: 13, color: "#92400e", margin: "12px 0 0" }}>
          <p style={{ margin: "0 0 4px" }}>
            {others.length} hosted database(s) still use another zone (until their next migrated version):
          </p>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {others.map(o => <li key={o.database}><code>{o.database}</code> (version {o.version}): {o.zone}</li>)}
          </ul>
        </div>
      )}
    </Card>
  );
}
